import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const fmt = d => d ? new Date(d).toLocaleDateString('fr-FR') : '?';

function calcJoursOuvres(startDate, endDate) {
  const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const feriesFixes = (y) => [`${y}-01-01`,`${y}-05-01`,`${y}-05-08`,`${y}-07-14`,`${y}-08-15`,`${y}-11-01`,`${y}-11-11`,`${y}-12-25`];
  const easterDate = (y) => {
    const a=y%19,b=Math.floor(y/100),c=y%100,d=Math.floor(b/4),e=b%4,f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3),h=(19*a+b-d-g+15)%30,i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7,m=Math.floor((a+11*h+22*l)/451),month=Math.floor((h+l-7*m+114)/31),day=((h+l-7*m+114)%31)+1;
    return new Date(y,month-1,day);
  };
  const feries = (y) => {
    const easter=easterDate(y),lundi=new Date(easter),asc=new Date(easter),pent=new Date(easter);
    lundi.setDate(easter.getDate()+1); asc.setDate(easter.getDate()+39); pent.setDate(easter.getDate()+50);
    return new Set([...feriesFixes(y),localISO(lundi),localISO(asc),localISO(pent)]);
  };
  const start = new Date(startDate), end = new Date(endDate);
  if (end < start) return '';
  const years = new Set([start.getFullYear(), end.getFullYear()]);
  const joursFeeries = new Set([...[...years].flatMap(y => [...feries(y)])]);
  let count = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const dow = cur.getDay(), iso = localISO(cur);
    if (dow !== 0 && dow !== 6 && !joursFeeries.has(iso)) count++;
    cur.setDate(cur.getDate()+1);
  }
  return String(count);
}

function Tooltip({ text }) {
  return (
    <span className="relative inline-block group align-middle ml-1">
      <span className="cursor-help text-blue-500 text-xs border border-blue-400 rounded-full w-4 h-4 inline-flex items-center justify-center font-bold">?</span>
      <span className="absolute z-20 left-1/2 -translate-x-1/2 bottom-full mb-2 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity leading-relaxed shadow-lg">
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  );
}

export default function CompanyFormPage() {
  const { token } = useParams();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [siretLoading, setSiretLoading] = useState(false);
  const [siretError, setSiretError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    siret: '',
    companyName: '',
    address: '',
    city: '',
    zipCode: '',
    apeCode: '',
    activityDomain: '',
    companyPhone: '',
    companyEmail: '',
    repCivilite: '',
    repName: '',
    repFirstName: '',
    repFunction: '',
    repPhone: '',
    tutorCivilite: '',
    tutorName: '',
    tutorFirstName: '',
    tutorFunction: '',
    tutorEmail: '',
    tutorPhone: '',
    gratification: '',
    gratificationPeriode: '',
    avantagesPrive: '',
    avantagesPublic: '',
    assureur: '',
    contratAssurance: '',
    schedule: '',
    totalDays: '',
    weeklyHours: '',
  });

  useEffect(() => {
    fetch(`/api/public/convention/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
        } else {
          setContext(data);
          if (data.company) {
            setForm(f => ({
              ...f,
              siret: data.company.siret || '',
              companyName: data.company.name || '',
              address: data.company.address || '',
              city: data.company.city || '',
              zipCode: data.company.zipCode || '',
              apeCode: data.company.apeCode || '',
              activityDomain: data.company.activityDomain || '',
              companyPhone: data.company.phone || '',
              companyEmail: data.company.email || '',
            }));
          }
          if (data.tutor) {
            setForm(f => ({
              ...f,
              tutorName: data.tutor.name || '',
              tutorFirstName: data.tutor.firstName || '',
              tutorFunction: data.tutor.function || '',
              tutorEmail: data.tutor.email || '',
              tutorPhone: data.tutor.phone || '',
            }));
          }
          if (data.representative) {
            setForm(f => ({
              ...f,
              repCivilite: data.representative.civilite || '',
              repName: data.representative.name || '',
              repFirstName: data.representative.firstName || '',
              repFunction: data.representative.function || '',
              repPhone: data.representative.phone || '',
            }));
          }
          if (data.tutorCivilite) {
            setForm(f => ({ ...f, tutorCivilite: data.tutorCivilite }));
          }
          setForm(f => ({
            ...f,
            gratification: data.gratification || '',
            gratificationPeriode: data.gratificationPeriode || '',
            avantagesPrive: data.avantagesPrive || '',
            avantagesPublic: data.avantagesPublic || '',
            assureur: data.assureur || '',
            contratAssurance: data.contratAssurance || '',
            totalDays: data.totalDays || (data.startDate && data.endDate ? calcJoursOuvres(data.startDate, data.endDate) : ''),
            weeklyHours: data.weeklyHours || '35',
          }));
        }
      })
      .catch(() => setError('Impossible de charger le formulaire.'))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const searchSiret = async () => {
    setSiretError(null);
    if (!/^\d{14}$/.test(form.siret)) {
      setSiretError('Le SIRET doit contenir exactement 14 chiffres.');
      return;
    }
    setSiretLoading(true);
    try {
      const r = await fetch(`/api/public/sirene/${form.siret}`);
      const data = await r.json();
      if (!r.ok) {
        setSiretError(data.error || 'SIRET introuvable.');
      } else {
        setForm(f => ({
          ...f,
          companyName: data.name || f.companyName,
          address: data.address || f.address,
          city: data.city || f.city,
          zipCode: data.zipCode || f.zipCode,
          apeCode: data.apeCode || f.apeCode,
          activityDomain: data.activityDomain || f.activityDomain,
        }));
        setSiretError(null);
      }
    } catch {
      setSiretError('Erreur lors de la recherche. Vérifiez votre connexion.');
    } finally {
      setSiretLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.siret || !form.companyName || !form.tutorEmail) {
      alert('SIRET, nom de l\'entreprise et email du tuteur sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`/api/public/convention/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) {
        alert(data.error || 'Erreur lors de l\'enregistrement.');
      } else {
        setSubmitted(true);
      }
    } catch {
      alert('Erreur réseau. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Lien invalide</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-green-100 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Formulaire envoyé</h1>
          <p className="text-gray-600">
            Les informations ont été enregistrées. L'enseignant référent va maintenant vérifier la convention de stage.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">S</span>
          </div>
          <span className="font-semibold text-gray-800">StageLinks</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Bandeau étudiant */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-700 font-medium mb-1">Convention de stage</p>
          <p className="text-gray-800 font-semibold">{context.studentName}</p>
          <p className="text-sm text-gray-600">{context.className}</p>
          {context.startDate && (
            <p className="text-sm text-gray-600 mt-1">
              {fmt(context.startDate)} → {fmt(context.endDate)}
            </p>
          )}
        </div>

        {/* Document annexe si disponible */}
        {context.annexePdf && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-4 mb-6">
            <div>
              <p className="text-sm font-medium text-amber-800">Document annexe à la convention</p>
              <p className="text-xs text-amber-600 mt-0.5">Consultez ce document fourni par l'établissement scolaire.</p>
            </div>
            <a
              href={`/uploads/${context.annexePdf}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-lg transition-colors"
            >
              {context.annexeBoutonTexte || 'Consulter annexe'}
            </a>
          </div>
        )}

        <p className="text-gray-600 text-sm mb-6">
          Veuillez remplir les informations concernant votre entreprise. Ces données seront intégrées à la convention de stage.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section SIRET */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Recherche par SIRET</h2>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="14 chiffres"
                maxLength={14}
                value={form.siret}
                onChange={set('siret')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={searchSiret}
                disabled={siretLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg disabled:opacity-50 shrink-0"
              >
                {siretLoading ? 'Recherche...' : 'Rechercher'}
              </button>
            </div>
            {siretError && (
              <p className="text-red-600 text-sm mt-2">{siretError}</p>
            )}
          </div>

          {/* Section Entreprise */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Informations entreprise</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise *</label>
                <input type="text" value={form.companyName} onChange={set('companyName')} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input type="text" value={form.address} onChange={set('address')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                  <input type="text" value={form.zipCode} onChange={set('zipCode')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                  <input type="text" value={form.city} onChange={set('city')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code APE</label>
                  <input type="text" value={form.apeCode} onChange={set('apeCode')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Domaine d'activité</label>
                  <input type="text" value={form.activityDomain} onChange={set('activityDomain')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone entreprise</label>
                  <input type="tel" value={form.companyPhone} onChange={set('companyPhone')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Section Représentant */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Représentant de l'entreprise</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Civilité</label>
                  <select value={form.repCivilite} onChange={set('repCivilite')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">—</option>
                    <option value="M.">M.</option>
                    <option value="Mme">Mme</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fonction</label>
                  <input type="text" value={form.repFunction} onChange={set('repFunction')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input type="text" value={form.repName} onChange={set('repName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input type="text" value={form.repFirstName} onChange={set('repFirstName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input type="tel" value={form.repPhone} onChange={set('repPhone')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email entreprise</label>
                  <input type="email" value={form.companyEmail} onChange={set('companyEmail')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Section Tuteur */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Tuteur de stage</h2>
            <p className="text-xs text-gray-500 mb-3">La personne qui encadrera directement le stagiaire au quotidien.</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Civilité</label>
                  <select value={form.tutorCivilite} onChange={set('tutorCivilite')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">—</option>
                    <option value="M.">M.</option>
                    <option value="Mme">Mme</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fonction</label>
                  <input type="text" value={form.tutorFunction} onChange={set('tutorFunction')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input type="text" value={form.tutorName} onChange={set('tutorName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input type="text" value={form.tutorFirstName} onChange={set('tutorFirstName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" value={form.tutorEmail} onChange={set('tutorEmail')} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input type="tel" value={form.tutorPhone} onChange={set('tutorPhone')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Section Gratification */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Gratification & Avantages</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant (€)</label>
                  <input type="number" step="0.01" min="0" value={form.gratification} onChange={set('gratification')}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
                  <select value={form.gratificationPeriode} onChange={set('gratificationPeriode')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Choisir...</option>
                    <option value="heure">Par heure</option>
                    <option value="jour">Par jour</option>
                    <option value="mois">Par mois</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Avantages accordés (droit privé — Art. 5bis)
                  <Tooltip text={"Article 5bis – Accès aux droits des salariés (Organisme de droit privé)\n\nLe stagiaire bénéficie des protections et droits mentionnés aux articles L.1121-1, L.1152-1 et L.1153-1 du code du travail, dans les mêmes conditions que les salariés.\n\nIl a accès au restaurant d'entreprise ou aux titres-restaurants (art. L.3262-1) et bénéficie de la prise en charge des frais de transport (art. L.3261-2).\n\nIl accède aux activités sociales et culturelles (art. L.2323-83) dans les mêmes conditions que les salariés."} />
                </label>
                <input type="text" value={form.avantagesPrive} onChange={set('avantagesPrive')}
                  placeholder="Tickets restaurant, remboursement transport…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Avantages accordés (droit public — Art. 5ter)
                  <Tooltip text={"Article 5ter – Accès aux droits des agents (Organisme de droit public)\n\nLes trajets entre le domicile et le lieu de stage sont pris en charge dans les conditions fixées par le décret n°2010-676 du 21 juin 2010.\n\nLe stagiaire effectuant une mission dans le cadre d'un organisme de droit public bénéficie de la prise en charge de ses frais de déplacement temporaire selon la réglementation en vigueur.\n\nEst considéré comme résidence administrative le lieu du stage indiqué dans la convention."} />
                </label>
                <input type="text" value={form.avantagesPublic} onChange={set('avantagesPublic')}
                  placeholder="Indemnités, avantages en nature…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* Section Assurance */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Assurance</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'assureur</label>
                <input type="text" value={form.assureur} onChange={set('assureur')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">N° de contrat</label>
                <input type="text" value={form.contratAssurance} onChange={set('contratAssurance')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* Section Horaires */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Horaires de travail</h2>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
              La durée de présence hebdomadaire ne peut excéder <strong>30 heures</strong> pour les stagiaires de moins de 15 ans et <strong>35 heures</strong> pour les stagiaires de plus de 15 ans.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de jours total</label>
                <input type="number" min="1" value={form.totalDays} onChange={set('totalDays')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <p className="text-xs text-gray-400 mt-1">Pré-calculé en jours ouvrés, modifiable</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heures hebdomadaires</label>
                <input type="number" min="1" max="35" value={form.weeklyHours} onChange={set('weeklyHours')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <textarea
              value={form.schedule}
              onChange={set('schedule')}
              rows={4}
              placeholder={"Ex :\nLundi - Vendredi : 9h00 - 12h30 / 13h30 - 17h00\nMercredi : 9h00 - 12h00"}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer et envoyer'}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6 mb-8">
          StageLinks — Formulaire sécurisé
        </p>
      </div>
    </div>
  );
}
